import { ArrowRightOutlined, LoadingOutlined, MailOutlined } from "@ant-design/icons";
import { Button, Card, Col, Empty, Form, Input, Layout, message, Result, Row, Spin, Typography } from "antd";
import { observer } from "mobx-react-lite";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { FloatingWhatsApp } from 'react-floating-whatsapp';
import supportAvatar from '../assets/support.jpg';
import { GlobalStore } from "../store/store";

const { Content } = Layout;

interface ICheckoutPageProps {
  store: GlobalStore;
};

export const CheckoutPage: FC<ICheckoutPageProps> = observer(({ store }) => {
  const [productID, setProductID] = useState<string | null>(null);
  const [intentID, setIntentID] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState<boolean | null>(null);

  useEffect(() => {
    if (store.products.length === 0) {
      store.getProducts();
    }

    // Check to see if this is a redirect back from Checkout
    const query = new URLSearchParams(window.location.search);

    if (query.get("success")) {
      //message.success("Pedido Recebido!");
      setSuccess(true);
    }

    if (query.get("canceled")) {
      message.warning("Pedido Cancelado -- continue com a compra quando estiver pronto.");
      setSuccess(false);
    }

    if (query.get("product_id")) {
      setProductID(query.get("product_id"));
    }

    if (query.get("intent_id")) {
      setIntentID(query.get("intent_id"));
    }

    setLoading(false);
  }, []);

  const product = useMemo(() => {
    if (!productID || store.products.length === 0) { return; }

    return store.products.find(p => p.ProductID === productID);
  }, [productID, store.products])

  return (
    <Layout>
      <Content style={{ padding: '0 50px 50px 50px', width: 1150, margin: "auto" }}>
        <div style={{ padding: 50 }}>
          {success === true &&
            <GoFarmSuccess product={product} intent_id={intentID} loading={store.loading || loading} store={store} />
          }

          {success === false &&
            <Empty description="Dados não encontrados" />
          }
        </div>
      </Content>
    </Layout>
  );
}
)

type CanSetPasswordResponse = {
  Wait: boolean,
  Can: boolean,
  Blocked: boolean,
  Redir: boolean,
  Email: string,
  ProductSerial: string,
  Success: boolean,
}

const fetchCanSetPassword = async (intent_id: string) => {
  try {
    const params = new URLSearchParams({ intent_id });
    const can_set_resp = await fetch("/api/password/check?" + params.toString(), {
      method: 'GET',
    });

    const can_set = await can_set_resp.json() as CanSetPasswordResponse;
    return can_set;
  } catch (e) {
    console.error(e);
    message.error("Tivemos um problema ao carregar dados da compra. Tente novamente ou entre em contato com o suporte Tecgraf.")
    return null;
  }
}

type SetPasswordRequest = {
  IntentID: string,
  Password: string,
}

type SetPasswordResponse = {
  Success: boolean,
}

const fetchSetPassword = async (body: SetPasswordRequest) => {
  try {
    const set_password_resp = await fetch("/api/password/set", {
      method: 'POST',
      body: JSON.stringify(body),
    });

    const set_password = await set_password_resp.json() as SetPasswordResponse;
    return set_password;
  } catch (e) {
    console.error(e);
    message.error("Tivemos um problema ao configurar sua senha. Tente novamente ou entre em contato com o suporte Tecgraf.")
    return null;
  }
}

type SuccessPageProps = {
  product: any,
  intent_id: string | null,
  loading: boolean,
  store: GlobalStore,
}

type SuccessPageState =
  { Loading: boolean, Creating: boolean } &
  CanSetPasswordResponse &
  SetPasswordResponse

type SynchronizeRequest = { IntentID: string };

const fetchSynchronize = async (body: SynchronizeRequest) => {
  try {
    await fetch("/api/synchronize", {
      method: 'POST',
      body: JSON.stringify(body),
    });

  } catch (e) {
    console.error(e);
    return null;
  }
}

const GoFarmSuccess = observer(({ product, intent_id, loading, store }: SuccessPageProps) => {
  const [success, setSuccess] = useState(false);
  const [state, setState] = useState<SuccessPageState>({
    Email: "",
    ProductSerial: '',
    Loading: false,
    Creating: false,
    Blocked: false,
    Can: false,
    Redir: false,
    Wait: false,
    Success: false,
  });

  const load = (intent_id: string) => {
    setState(state => ({ ...state, Loading: true }));

    fetchCanSetPassword(intent_id)
      .then((resp) => {
        setState(state => ({ ...state, ...resp, Loading: false }))
        setSuccess(resp?.Success ?? false);

        if (!resp?.Success && resp?.Wait) {
          fetchSynchronize({ IntentID: intent_id });
        }

        if ((!resp?.Can && !resp?.Redir && !resp?.Success) || resp?.Blocked) {
          if (timeout_id.current) {
            clearTimeout(timeout_id.current);
          }
        }
      });
  }

  useEffect(() => {
    if (!intent_id) { return; }

    load(intent_id);
  }, [intent_id]);

  const timeout_id = useRef<NodeJS.Timeout | null>(null)
  useEffect(() => {
    if ((intent_id && !state.Can && !state.Redir && !state.Blocked && !(state.Success || success))
      || (intent_id && state.Blocked && !state.ProductSerial)) {
      if (timeout_id.current) {
        clearTimeout(timeout_id.current);
      }
      timeout_id.current = setTimeout(() => load(intent_id), 10000);
    }
  }, [state, intent_id]);

  const formSubmit = async (v: any) => {
    const body: SetPasswordRequest = {
      IntentID: intent_id!,
      Password: v.password,
    };

    setState(state => ({ ...state, Creating: true }));
    const resp = await fetchSetPassword(body);
    setState(state => ({ ...state, ...resp, Creating: false }));
    setSuccess(resp?.Success ?? false);

    if (resp?.Success) {
      message.success("Dados de acesso cadastrados com sucesso!")
    } else {
      message.error("Houve um problema ao cadastrar os dados de acesso. Tente novamente, ou entre em contato com o suporte Tecgraf.")
    }
  }

  const display_loading = loading || state.Wait || state.Loading;

  const sendEmail = () => {
    if (!intent_id) { return; }

    store.sendMail(intent_id)
  }

  const [wait_time, set_wait_time] = useState(0);
  useEffect(() => {
    if (store.send_state?.WaitTime && store.send_state.WaitTime > 0) {
      set_wait_time(store.send_state.WaitTime);
    }

    if (store.send_state?.Success) {
      message.success("E-mail enviado com sucesso, confira sua caixa de entrada.")
    }
  }, [store.send_state]);

  useEffect(() => {
    if (wait_time > 0) {
      setTimeout(() => set_wait_time(wait_time - 1), 1000);
    }
  }, [wait_time]);

  return (
    <div>
      <Row gutter={30}>
        <Col md={12} style={{ display: "flex", alignItems: "center" }}>
          <img src="img/gofarm.png" style={{ height: 360 }} />
        </Col>

        <Col md={12}>
          <Card>
            <Result
              status="success"
              title={`${product?.Data?.Name ?? "GoFarm®"} - Pedido Confirmado `}
            />

            {(display_loading && !(state.Can && !state.Success) && !state.Blocked) &&
              <div style={{ textAlign: "center", marginBottom: 48 }}>
                <Spin indicator={<LoadingOutlined />} />
              </div>}

            {(state.Can && !state.Success) &&
              <>
                <Typography style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 14 }}>
                  Cadastre abaixo os dados de acesso ao GoFarm®.
                </Typography>

                <Form
                  layout="vertical"
                  onFinish={formSubmit}
                >
                  <Typography.Title level={5}>
                    Dados de Acesso
                  </Typography.Title>

                  <Form.Item label="E-Mail">
                    <Input disabled value={state.Email} />
                  </Form.Item>

                  <Form.Item
                    label="Senha"
                    required
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: 'Por favor, digite a senha de acesso.',
                      },
                    ]}
                  >
                    <Input.Password />
                  </Form.Item>

                  <Form.Item
                    label="Confirmar Senha"
                    required
                    name="confirm_password"
                    rules={[
                      {
                        required: true,
                        message: 'Por favor, confirme a senha de acesso.',
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('Senhas devem ser iguais!'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password />
                  </Form.Item>

                  <div style={{ height: 10 }} />

                  <Form.Item>
                    <Button type="primary" htmlType="submit" style={{ width: 150 }}>
                      Cadastrar {state.Creating && <Spin indicator={<LoadingOutlined style={{ color: "#fff" }} />} />}
                    </Button>
                  </Form.Item>
                </Form>
              </>}

            {state.ProductSerial &&
              <Typography style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 16, textAlign: 'center', marginBottom: 40, marginTop: -32 }}>
                {state.ProductSerial}
              </Typography>}

            {state.Success &&
              <>
                <Typography style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 16, marginBottom: 20 }}>
                  Dados de acesso cadastrados com sucesso.<br /><br />
                  Guarde sua senha pois ela será necessária para acesso ao GoFarm®.<br /><br />
                  Enviamos um e-mail para {state.Email} com as instruções de download e acesso ao software.<br /><br />
                </Typography>

                <Typography style={{ color: "rgba(0, 0, 0, 0.88)", fontSize: 18, marginBottom: 40 }}>
                  Agradecemos o seu pedido, e conte com o nosso suporte.
                </Typography>
              </>}

            {(!state.Success && state.Blocked) &&
              <Typography style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 16, textAlign: 'center', marginBottom: 40 }}>
                Acesse o Painel de Gerente para criar o login para o novo usuário, conforme link e instruções recebidas no e-mail que acabamos de enviar.<br />
                E-Mail: {state.Email}
              </Typography>}

            {(!state.Success && !state.Blocked && display_loading) &&
              <Typography style={{ color: "rgba(0, 0, 0, 0.6)", fontSize: 16, textAlign: 'center', marginBottom: 40 }}>
                Protocolo: {intent_id}.<br />
                A confirmação de pagamento pode levar até 5 minutos, por favor aguarde nesta tela.
              </Typography>}

            {(state.Redir || state.Blocked) &&
              <div style={{ textAlign: "center", marginTop: -10, marginBottom: 40 }}>
                <Button type="primary" key="console" onClick={() => window.location.href = "https://secure.tecgraf.com.br/sess"}>
                  Acessar Painel <ArrowRightOutlined />
                </Button>
              </div>}

            {(state.Blocked || state.Success) &&
              <div style={{ textAlign: "center", marginTop: -10, marginBottom: 40 }}>
                <Button type="default" key="console" onClick={() => sendEmail()} disabled={store.sending || wait_time > 0}>
                  {store.sending && <Spin indicator={<LoadingOutlined style={{ fontSize: 14 }} />} />}
                  {!store.sending &&
                    (wait_time > 0) ?
                    <>
                      ({wait_time}) Aguarde um pouco <MailOutlined />
                    </>
                    :
                    <>
                      Reenviar E-mail <MailOutlined />
                    </>
                  }
                </Button>
              </div>}

            {/* <div style={{ display: "flex", justifyContent: "space-between" }}> */}
            {/* <Button type="link" onClick={() => setVideo(true)} style={{ width: 150 }}>
                Tutorial de Acesso
              </Button> */}
            {/* {!state.Can &&
                <Button type="link" style={{ width: 150 }}>
                  Esqueceu a senha?
                </Button>} */}
            {/* </div> */}
          </Card>
        </Col>
      </Row>

      <FloatingWhatsApp
        accountName="Suporte Tecgraf Agro"
        phoneNumber="+5519999174690"
        avatar={supportAvatar}
        statusMessage="Responde em poucos minutos"
        chatMessage={`Bem-vindo ao suporte Tecgraf Agro! 🤝

Como podemos ajudar?`}
        placeholder="Digite sua mensagem"
      />
    </div>
  );
})